import { forwardRef } from "react";
import { Button } from "@/modern/components/ui/button-shadcn";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/modern/components/ui/dropdown-menu";
import { Check, Palette } from "lucide-react";
import { cn } from "../utils";
import { useTheme } from "../hooks/useTheme";
import { AVAILABLE_THEMES, type ThemeKey } from "@/modern/constants/themeConstants";

export const ThemeSwitcher = forwardRef<HTMLButtonElement, { className?: string, triggerButtonContent?: (themeName: string) => React.ReactNode; dropDownMenuContentClassName?: string }>((props, ref) => {
  // Use theme context instead of local state
  const { theme, setTheme } = useTheme();

  const currentThemeLabel = AVAILABLE_THEMES.find(t => t.key === theme)?.label || "Default";

  const handleThemeChange = (newTheme: ThemeKey) => {
    if (newTheme !== theme) {
      setTheme(newTheme);
    }
  };

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={cn("px-2", props.className)}
          ref={ref}
          title={currentThemeLabel}
        >
          {props.triggerButtonContent?.(currentThemeLabel) || (
            <Palette className="h-4 w-4" />
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className={cn("w-[240px] max-h-[500px] z-[--swivu-bryntum-z-index]", props.dropDownMenuContentClassName)}>
        <DropdownMenuLabel className="flex items-center gap-2">
          <Palette className="h-4 w-4" />
          Theme Style
        </DropdownMenuLabel>
        {AVAILABLE_THEMES.map((themeDefinition) => {
          const { key, label, colors } = themeDefinition;
          const isSelected = theme === key;

          return (
            <DropdownMenuItem
              key={key}
              onClick={() => handleThemeChange(key)}
              className="cursor-pointer focus:bg-accent"
            >
              <div className="flex items-center justify-between w-full">
                <div className="flex items-center gap-2.5">
                  <div className="flex gap-0.5">
                    <div
                      className="w-4 h-4 rounded-sm border border-border/50 shadow-sm"
                      style={{ backgroundColor: colors.primary }}
                      title="Primary color"
                    />
                    <div
                      className="w-4 h-4 rounded-sm border border-border/50 shadow-sm"
                      style={{ backgroundColor: colors.secondary }}
                      title="Secondary color"
                    />
                    <div
                      className="w-4 h-4 rounded-sm border border-border/50 shadow-sm"
                      style={{ backgroundColor: colors.accent }}
                      title="Accent color"
                    />
                  </div>
                  <span className="font-medium">{label}</span>
                </div>
                {isSelected && <Check className="h-4 w-4 text-primary" />}
              </div>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
});

ThemeSwitcher.displayName = "ThemeSwitcher";
